import { lazy, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'
import CookieConsent from './components/CookieConsent'
import InteriorNavigation from './components/InteriorNavigation'
import LocaleProvider from './i18n/LocaleProvider'
import { DEFAULT_LOCALE, LOCALES } from './i18n/locales'
import { useLocalPathname } from './i18n/Link'

// Lazy load - public pages
const Home = lazy(() => import('./pages/Home'))
const Features = lazy(() => import('./pages/Features'))
const Compare = lazy(() => import('./pages/Compare'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Contact = lazy(() => import('./pages/Contact'))
const Download = lazy(() => import('./pages/Download'))
const About = lazy(() => import('./pages/About'))
const Blog = lazy(() => import('./pages/Blog'))
const Careers = lazy(() => import('./pages/Careers'))
const Press = lazy(() => import('./pages/Press'))
const Help = lazy(() => import('./pages/Help'))
const Docs = lazy(() => import('./pages/Docs'))
const Community = lazy(() => import('./pages/Community'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const Security = lazy(() => import('./pages/Security'))
const Cookies = lazy(() => import('./pages/Cookies'))
const Enterprise = lazy(() => import('./pages/Enterprise'))
const Partners = lazy(() => import('./pages/Partners'))
const Status = lazy(() => import('./pages/Status'))
const Changelog = lazy(() => import('./pages/Changelog'))
const NotFound = lazy(() => import('./pages/NotFound'))

// Lazy load - admin
const AuthProvider = lazy(() => import('./admin/context/AuthContext').then((m) => ({ default: m.AuthProvider })))
const ProtectedRoute = lazy(() => import('./admin/components/ProtectedRoute'))
const AdminLayout = lazy(() => import('./admin/components/AdminLayout'))
const AdminLogin = lazy(() => import('./admin/pages/Login'))
const Dashboard = lazy(() => import('./admin/pages/Dashboard'))
const BlogManager = lazy(() => import('./admin/pages/BlogManager'))
const PricingManager = lazy(() => import('./admin/pages/PricingManager'))
const DownloadsManager = lazy(() => import('./admin/pages/DownloadsManager'))
const CareersManager = lazy(() => import('./admin/pages/CareersManager'))
const HelpManager = lazy(() => import('./admin/pages/HelpManager'))
const CommunityManager = lazy(() => import('./admin/pages/CommunityManager'))
const ContactManager = lazy(() => import('./admin/pages/ContactManager'))
const NewsletterManager = lazy(() => import('./admin/pages/NewsletterManager'))
const ChangelogManager = lazy(() => import('./admin/pages/ChangelogManager'))
const LicensePackages = lazy(() => import('./admin/pages/LicensePackages'))

const INTERIOR_V2 = ['/features', '/pricing', '/download', '/contact']

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

/** The public site for one language. Routes are relative to the locale prefix. */
function PublicSite() {
  const { t } = useTranslation('common')
  const pathname = useLocalPathname()
  const isHome = pathname === '/'
  const mainClass = isHome
    ? ''
    : `interior-main route-${pathname.slice(1)} ${INTERIOR_V2.includes(pathname) ? '' : 'legacy-interior'}`

  return (
    <div className="public-site min-h-screen bg-dark text-light overflow-x-clip">
      <a className="skip-link" href="#main-content">
        {t('nav.skip')}
      </a>
      <div className="fixed inset-0 bg-grid pointer-events-none" />
      <div className="fixed inset-0 bg-glow pointer-events-none" />

      <ScrollToTop />
      <Navbar />

      <main id="main-content" tabIndex={-1} className={`relative z-10 min-h-screen ${mainClass}`}>
        {!isHome && <InteriorNavigation />}
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route index element={<Home />} />
            <Route path="features" element={<Features />} />
            <Route path="compare" element={<Compare />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="download" element={<Download />} />
            <Route path="contact" element={<Contact />} />
            <Route path="about" element={<About />} />
            <Route path="blog" element={<Blog />} />
            <Route path="careers" element={<Careers />} />
            <Route path="press" element={<Press />} />
            <Route path="help" element={<Help />} />
            <Route path="docs" element={<Docs />} />
            <Route path="community" element={<Community />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="security" element={<Security />} />
            <Route path="cookies" element={<Cookies />} />
            <Route path="enterprise" element={<Enterprise />} />
            <Route path="partners" element={<Partners />} />
            <Route path="status" element={<Status />} />
            <Route path="changelog" element={<Changelog />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  )
}

function App() {
  const location = useLocation()
  const isAdmin = location.pathname.startsWith('/admin')

  if (isAdmin) {
    return (
      <Suspense fallback={<Loading />}>
        <AuthProvider>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="blog" element={<BlogManager />} />
              <Route path="pricing" element={<PricingManager />} />
              <Route path="downloads" element={<DownloadsManager />} />
              <Route path="careers" element={<CareersManager />} />
              <Route path="help" element={<HelpManager />} />
              <Route path="community" element={<CommunityManager />} />
              <Route path="contact" element={<ContactManager />} />
              <Route path="newsletter" element={<NewsletterManager />} />
              <Route path="changelog" element={<ChangelogManager />} />
              <Route path="license-packages" element={<LicensePackages />} />
            </Route>
          </Routes>
        </AuthProvider>
      </Suspense>
    )
  }

  // One route tree per language: /en/*, /de/*, ... and the default (Turkish) at /*
  return (
    <Routes>
      {LOCALES.filter((l) => l.code !== DEFAULT_LOCALE).map((l) => (
        <Route
          key={l.code}
          path={`/${l.code}/*`}
          element={
            <LocaleProvider code={l.code}>
              <PublicSite />
            </LocaleProvider>
          }
        />
      ))}
      <Route
        path="/*"
        element={
          <LocaleProvider code={DEFAULT_LOCALE}>
            <PublicSite />
          </LocaleProvider>
        }
      />
    </Routes>
  )
}

export default App
